import { useEffect, useRef } from 'react';

import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { StagedContentLinkClickCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/staged-content-link-click-commands';
import { isWebviewContext } from '@atlassian/rovo-content-bridge-api/desktop-apis';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import { useStagedContentLinkInterceptor } from '@atlassian/smart-creation/use-staged-content-link-interceptor';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useChatStore } from '../../chat-store/useChatStore';

/**
 * Answers `StagedContentLinkClickCommand`s sent by content from the iframe: a smart-link
 * whose URL points to an unfinalized staged item in this conversation selects that item
 * in the staging area, exactly as the same click on a smart-link card in chat does.
 *
 * The editor runs in an iframe without the same conversation/staging stores, so it
 * can't resolve the click itself. This hook reuses the same interceptor Rovo chat mounts in
 * `ConversationAssistantRender`, fed the same inputs, and returns its handled result.
 */
export const useStagedContentLinkClickHandler = (): void => {
	const [{ currentConversationId }] = useChatStore();
	const { elements, fullScreen } = useUIConfig();
	const { productKey } = useGlobalConfig();

	const interceptLinkNavigation = useStagedContentLinkInterceptor({
		conversationId: currentConversationId,
		productKey,
		stagingAreaEnabled: elements?.stagingAreaEnabled,
		fullScreen,
	});

	// Ref so the registration below is not torn down and re-created whenever the interceptor's
	// inputs (e.g. the conversation id) change.
	const interceptLinkNavigationRef = useRef(interceptLinkNavigation);
	interceptLinkNavigationRef.current = interceptLinkNavigation;

	useEffect(() => {
		// Same guard as the other Rovo chat handlers: desktop implements this client itself.
		if (isWebviewContext()) {
			return;
		}

		const bridgeAPI = getBrowserBridgeAPI();

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, StagedContentLinkClickCommand, (command) =>
			// Modifier-clicks never reach the host: the editor keeps its default behaviour for them.
			interceptLinkNavigationRef.current(command.url, { modifierKey: false, source: 'smartLink' }),
		);

		return () => {
			bridgeAPI.off(ROVO_CHAT_CLIENT_ID, StagedContentLinkClickCommand);
		};
	}, []);
};
